import { Alert, Badge, Card, Group, Stack, Switch, Text, Title } from "@mantine/core";
import { notifications } from "@mantine/notifications";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { semanticColor } from "@how/design-system";
import { ConfirmActionModal, LoadingState } from "@how/ui";
import { supabase } from "../../lib/supabase";
import { formatUsNumber } from "./TwilioCard";

/** A church's pilot texting as `platform_pilot_texting` reports it (ADR 0072). */
export interface PilotTexting {
  /**
   * Whether pilot texting can run in this environment at all: staging only (ADR 0075). The database decides it and
   * enforces it; the card only follows what it says.
   */
  readonly available: boolean;
  readonly enabled: boolean;
  /** This church owns DCX's pilot connection: its texts already go out as the registered sender. */
  readonly isPilotSenderChurch: boolean;
  readonly sender: {
    readonly name: string;
    readonly from: string | null;
    readonly church: string;
    readonly live: boolean;
  } | null;
  /** How many numbers are opted-in pilot testers (PILOT then YES, or the pilot sign-up). */
  readonly testers: number;
}

/** The database's words, and the card's, anywhere but staging (ADR 0075). */
export const STAGING_ONLY = "Pilot texting is available on staging only";

/** Words for a refusal from set_pilot_texting, as a DCX super admin needs them. */
export function pilotTextingErrorMessage(error: { code?: string; message: string }): string {
  if (error.code === "23514") return error.message;
  if (error.code === "42501" && error.message === STAGING_ONLY) return `${STAGING_ONLY}.`;
  if (error.code === "42501")
    return "Only a DCX super admin signed in with two-step verification, outside a support session on this church, can change pilot texting.";
  if (error.code === "P0002") return "This church was not found.";
  return "Pilot texting was not changed. Try again.";
}

function usePilotTexting(tenantId: string) {
  return useQuery({
    queryKey: ["platform-pilot-texting", tenantId],
    queryFn: async (): Promise<PilotTexting> => {
      const { data, error } = await supabase.rpc("platform_pilot_texting", {
        p_tenant_id: tenantId,
      });
      if (error) throw error;
      return data as unknown as PilotTexting;
    },
  });
}

/**
 * Pilot texting (owner decision 2026-10-10, ADR 0072): a super admin lets a demo or pilot church send real test texts
 * through DCX Logic's approved pilot campaign. Every text then ends with the registered sender's name, goes out from
 * the pilot number, and reaches only people who agreed to the church's texts and opted in to the pilot. Switching it
 * on asks first; the database refuses anyone but a super admin at aal2 outside a support session, and audits it.
 */
export function PilotTextingCard({
  tenantId,
  tenantName,
}: {
  readonly tenantId: string;
  readonly tenantName: string;
}) {
  const client = useQueryClient();
  const state = usePilotTexting(tenantId);
  const [confirming, setConfirming] = useState(false);
  const change = useMutation({
    mutationFn: async (enabled: boolean): Promise<PilotTexting> => {
      const { data, error } = await supabase.rpc("set_pilot_texting", {
        p_tenant_id: tenantId,
        p_enabled: enabled,
      });
      if (error) throw new Error(pilotTextingErrorMessage(error));
      return data as unknown as PilotTexting;
    },
    onSuccess: (next) => {
      client.setQueryData(["platform-pilot-texting", tenantId], next);
      notifications.show({
        color: "teal",
        title: next.enabled ? "Pilot texting on" : "Pilot texting off",
        message: next.enabled
          ? `${tenantName}'s texts now go out from ${next.sender?.name ?? "the pilot sender"} to pilot testers only.`
          : `${tenantName} no longer sends through the pilot number.`,
      });
    },
  });

  const data = state.data;
  const senderName = data?.sender?.name ?? "the registered pilot sender";
  // Outside staging there is nothing to change (ADR 0075): no switch, no sender, no confirmation. The database refuses
  // switching it on there whatever the screen shows; this keeps the screen honest about it.
  if (data !== undefined && !data.available) {
    return (
      <Card>
        <Stack gap="sm">
          <Group justify="space-between" align="flex-start" gap="xs">
            <Title order={2} size="h4">
              Pilot texting
            </Title>
            <Badge variant="light" color={semanticColor("neutral")} style={{ flexShrink: 0 }}>
              Staging only
            </Badge>
          </Group>
          <Text size="sm" fw={600} data-testid="pilot-texting-staging-only">
            {STAGING_ONLY}.
          </Text>
          <Text size="sm" c="dimmed" maw={720}>
            Here, every church texts through its own campaign, registered to that church. DCX
            Logic&apos;s pilot campaign is used only on staging, for demos and pilots.
          </Text>
        </Stack>
      </Card>
    );
  }
  return (
    <Card>
      <Stack gap="sm">
        <Group justify="space-between" align="flex-start" gap="xs">
          <Title order={2} size="h4">
            Pilot texting
          </Title>
          {data === undefined ? null : (
            <Badge
              variant="light"
              color={semanticColor(data.enabled ? "info" : "neutral")}
              style={{ flexShrink: 0 }}
            >
              {data.enabled ? "On" : "Off"}
            </Badge>
          )}
        </Group>
        <Text size="sm" c="dimmed" maw={720}>
          Lets this church send real test texts through DCX Logic&apos;s approved pilot campaign.
          Every text goes out from the pilot number and ends with the registered sender&apos;s name,
          never the church&apos;s, and reaches only people who agreed to the church&apos;s texts and
          opted in to the pilot themselves. Everyone else is skipped.
        </Text>
        <Alert color={semanticColor("warning")} variant="light" title="For demos and pilots only">
          Never use pilot texting to reach a church&apos;s members or the public. A church texting
          its own people needs its own registration.
        </Alert>
        {state.isPending ? (
          <LoadingState rows={2} />
        ) : state.isError || data === undefined ? (
          <Alert color={semanticColor("danger")} variant="light">
            Could not read pilot texting for this church.
          </Alert>
        ) : (
          <Stack gap="xs">
            <Group gap="xl">
              <div>
                <Text size="xs" c="dimmed">
                  Registered sender
                </Text>
                <Text size="sm">{data.sender === null ? "Not set up" : data.sender.name}</Text>
              </div>
              <div>
                <Text size="xs" c="dimmed">
                  Pilot number
                </Text>
                <Text size="sm" className="how-numeric">
                  {data.sender === null || data.sender.from === null
                    ? "None"
                    : formatUsNumber(data.sender.from)}
                </Text>
              </div>
              <div>
                <Text size="xs" c="dimmed">
                  Opted-in pilot testers
                </Text>
                <Text size="sm" className="how-numeric">
                  {data.testers.toLocaleString()}
                </Text>
              </div>
            </Group>
            {data.isPilotSenderChurch ? (
              <Text size="sm">
                This church owns the pilot number: its texts already go out as {senderName}.
              </Text>
            ) : (
              <Switch
                label="Pilot texting"
                description={
                  data.sender === null || !data.sender.live
                    ? "No live pilot sender is set up yet (see the text messaging runbook)."
                    : `Texts go out from ${data.sender.name} to opted-in pilot testers only.`
                }
                checked={data.enabled}
                disabled={
                  change.isPending || (!data.enabled && (data.sender === null || !data.sender.live))
                }
                onChange={(event) => {
                  const on = event.currentTarget.checked;
                  if (on) setConfirming(true);
                  else change.mutate(false);
                }}
              />
            )}
            {change.isError ? (
              <Alert color={semanticColor("danger")} variant="light">
                {change.error.message}
              </Alert>
            ) : null}
          </Stack>
        )}
      </Stack>
      <ConfirmActionModal
        opened={confirming}
        onClose={() => {
          setConfirming(false);
        }}
        title={`Turn on pilot texting for ${tenantName}?`}
        message={`${tenantName}'s texts will go out from ${senderName}'s number, end "${senderName}: Reply STOP to opt out", and reach only opted-in pilot testers. Use it for demos and pilots only.`}
        confirmLabel="Turn on pilot texting"
        loading={change.isPending}
        onConfirm={async () => {
          await change.mutateAsync(true).catch(() => undefined);
          setConfirming(false);
        }}
      />
    </Card>
  );
}
